/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */
import { EventBusModule } from "@vlinder-web/event-bus-module-react";
import { FSMFactoryModule } from "@vlinder-web/state-machine-factory-module-react";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { WalletconnectModule } from "@vlinder-web/walletconnect-module-react";
// import { WalletconnectModule } from "../modules/WalletConnect";
import { BaseApplication } from "./base/baseApplication";
import { UnitTest } from "./unit.test";

import {
    TrafficLightService,
    DeviceInfoService,
    DummyRestService,
    DummyGraphQLService,
    CollectionsService,
    AssetDetailService,
    ContactUsRestService,
    WalletApiService,
    WalletConnectClientService,
    FiatDetailService,
    ProfileService,
    AuthService,
    PdfService,
    HeaderNavService,
    UserDetailService,
    UserProfileService,
    TragProvenanceService,
    MyWalletService,
    MyAssetDetailService,
    LikeService
} from "../services";
import { StorageModule } from "@vlinder-web/storage-module-react";


export class VApplication extends BaseApplication {
    private static instance: VApplication;

    private constructor(options: any = {}) {
        super(options);

        this.setupBindings();

        //Modules
        this.initModules();

        //Services
        this.initServices();
    }

    static getInstance(options: any = {}) {
        if (!this.instance) {
            this.instance = new VApplication(options);
        }
        return this.instance;
    }

    // @ts-ignore
    async start() {
        await this.initRunLevel();
        await this.unitTest();
    }

    setupBindings() {        
        const storageService = StorageModule.getInstance();
        const DEFAULT_APP_CONFIG = storageService.get("appConfig", "session");
        // console.log(`insetupBinding app config`, DEFAULT_APP_CONFIG);
        //------------->>>>>--------------//
        // this.configure("services.DummyRestService", {
        //     baseURL: DEFAULT_APP_CONFIG?.servers.rest.restServer.baseURL,
        // });
        this.configure("services.ContactUsRestService", {
            baseURL: DEFAULT_APP_CONFIG?.servers.rest?.restServer.baseURL,
        });

        this.configure("services.CollectionsService", {
            imageServer: DEFAULT_APP_CONFIG?.servers?.rest?.imageServer,
            store:DEFAULT_APP_CONFIG?.organization?.storeId
        });

        this.configure("services.HeaderNavService", {
            categories: DEFAULT_APP_CONFIG?.organization?.categories,
            mintpadURL: DEFAULT_APP_CONFIG?.organization?.mintpadUrl,
            faqURL:DEFAULT_APP_CONFIG?.organization?.faqURL
        });

        this.configure("services.FiatDetailService", {
            key: DEFAULT_APP_CONFIG?.servers?.rest?.paymentGatewayServer?.keyId,
            baseURL:DEFAULT_APP_CONFIG?.servers?.rest?.paymentGatewayServer?.baseURL,
            scriptURL: DEFAULT_APP_CONFIG?.servers?.rest?.paymentGatewayServer?.scriptURL,
            storeId: DEFAULT_APP_CONFIG?.organization?.storeId,
        });
        
        this.configure("services.MyAssetDetailService", {
            storeId: DEFAULT_APP_CONFIG?.organization?.storeId,
            imageServer: DEFAULT_APP_CONFIG?.servers?.rest?.imageServer,
        });

        this.configure("services.AssetDetailService", {
            storeId: DEFAULT_APP_CONFIG?.organization?.storeId,
            imageServer: DEFAULT_APP_CONFIG?.servers?.rest?.imageServer,
        });

        this.configure("services.ProfileService", {
            imageServer: DEFAULT_APP_CONFIG?.servers?.rest?.imageServer,
        });

        this.configure("services.WalletconnectModule", {
            bridge: DEFAULT_APP_CONFIG ? DEFAULT_APP_CONFIG?.walletConnect?.relayURL : "https://walletbridge.vlinder.io/",
        });
        //------------->>>>>--------------//
    }

    async initRunLevel() {
        const storageSrv = StorageModule.getInstance();
        const DEFAULT_APP_CONFIG = storageSrv.get("appConfig", "session");
        //------------->>>>>--------------//
        const graphqlModule = this.getSync(
            "services.GraphqlServerModule"
        ) as GraphqlServerModule;
        if (graphqlModule) {
            type TClient = typeof DEFAULT_APP_CONFIG.servers.graphql[0];
            DEFAULT_APP_CONFIG.servers.graphql.forEach(
                async (client: TClient) => {
                    await graphqlModule.createClient(client.name, {
                        url: client.url,
                        getContext: this.getGraphQLContext.bind(this),
                    }).then(res=>{
                        
                    }).catch(err=>{
                        console.warn(err);
                    });
                }
            );
        }
        //------------->>>>>--------------//

        //------------->>>>>--------------//
        const wcClientService = this.getSync(
            "services.WalletConnectClientService"
        ) as WalletConnectClientService;
        await wcClientService.init();
        //------------->>>>>--------------//

        //------------->>>>>--------------//
        // const wcModule = this.getSync(
        //     "services.WalletconnectModule"
        // ) as WalletconnectModule;
        // await wcModule.init();
        //------------->>>>>--------------//
        const storageService = this.getSync(
            "services.StorageModule"
        ) as StorageModule;
        await storageService.init();
    }

    async unitTest() {
        const ut = new UnitTest(VApplication);
        ut.run();
    }

    initModules() {
        this.service("services.FSMFactoryModule", FSMFactoryModule);
        this.service("services.EventBusModule", EventBusModule);
        this.service("services.GraphqlServerModule", GraphqlServerModule);
        this.service("services.WalletconnectModule", WalletconnectModule);
        this.service("services.StorageModule", StorageModule);
    }

    initServices() {
        this.service("services.AuthService", AuthService);
        this.service("services.TrafficLightService", TrafficLightService);
        // this.service("services.DummyRestService", DummyRestService);
        this.service("services.DeviceInfoService", DeviceInfoService);
        this.service("services.DummyGraphQLService", DummyGraphQLService);
        this.service("services.CollectionsService", CollectionsService);
        this.service("services.UserDetailService", UserDetailService);
        this.service("services.ContactUsRestService", ContactUsRestService);
        this.service("services.AssetDetailService", AssetDetailService);
        this.service(
            "services.WalletConnectClientService",
            WalletConnectClientService
        );
        this.service("services.WalletApiService", WalletApiService);
        this.service("services.FiatDetailService", FiatDetailService);
        this.service("services.ProfileService", ProfileService);
        this.service("services.PdfService", PdfService);
        this.service("services.HeaderNavService", HeaderNavService);
        this.service("services.UserProfileService", UserProfileService);
        this.service("services.TragProvenanceService", TragProvenanceService);
        this.service("services.MyWalletService", MyWalletService);
        this.service("services.MyAssetDetailService", MyAssetDetailService);
        this.service("services.LikeService", LikeService);
    }

    private getGraphQLContext() {
        // const loginService = this.getSync("services.AuthService") as AuthService;
        const storageSrv = StorageModule.getInstance();
        const localToken = storageSrv.get('localToken', 'local')?.value;
        const sessionToken = storageSrv.get('sessionToken', 'session')?.value;

        const storageService = this.getSync("services.StorageModule");
        const DEFAULT_APP_CONFIG = storageService.get("appConfig", "session");
        
        const token = localToken ?? sessionToken;
        console.log('authtoken in service', token)
        const deviceInfoService = this.getSync(
            "services.DeviceInfoService"
        ) as DeviceInfoService;
        
        //FIXME:
        if (token) {
            return {
                headers: {
                    authentication: token,
                    "store-id": DEFAULT_APP_CONFIG?.organization?.storeId,
                    headers: {
                        ...{ ...deviceInfoService?.getDeviceInfo() },
                        ...{ ...deviceInfoService?.getAppInfo() },
                    },
                },
            };
        }
        else {
            return {
                headers: {
                    "store-id": DEFAULT_APP_CONFIG?.organization?.storeId,
                    headers: {
                        ...{ ...deviceInfoService?.getDeviceInfo() },
                        ...{ ...deviceInfoService?.getAppInfo() },
                    },
                },
            }
        }

    }
}
